/**
 * RBAC Authorization Utilities
 *
 * Pure utility functions for role and permission checking.
 *
 * Task: 34.1 - Extract Authorization Utilities
 */

import {
  UserActiveRole,
  UserPermission,
  RoleName,
  PermissionName,
  ResourceType,
  ActionType,
} from '@/types/rbac';

// ──────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────

/**
 * Admin role names for privilege checking
 */
const ADMIN_ROLES: RoleName[] = ['super_admin', 'admin'];

/**
 * Role hierarchy constants
 */
export const ROLE_HIERARCHY = {
  ADMIN_ROLES,
} as const;

// ──────────────────────────────────────────────────────
// Role Functions
// ──────────────────────────────────────────────────────

/**
 * Check if user has a specific role
 */
export function hasRole(roles: UserActiveRole[], roleName: RoleName): boolean {
  return roles.some(role => role.role_name === roleName);
}

/**
 * Check if user has any of the specified roles
 */
export function hasAnyRole(roles: UserActiveRole[], roleNames: RoleName[]): boolean {
  return roles.some(role => roleNames.includes(role.role_name as RoleName));
}

/**
 * Calculate highest role level from user's roles
 */
export function calculateHighestRoleLevel(roles: UserActiveRole[]): number {
  return roles.reduce((max, role) => Math.max(max, role.role_level), 0);
}

/**
 * Check if user has admin privileges
 */
export function isAdminUser(roles: UserActiveRole[]): boolean {
  return hasAnyRole(roles, ADMIN_ROLES);
}

// ──────────────────────────────────────────────────────
// Permission Functions
// ──────────────────────────────────────────────────────

/**
 * Check if user has a specific named permission
 */
export function hasPermission(
  permissions: UserPermission[],
  permissionName: PermissionName
): boolean {
  return permissions.some(perm => perm.permission_name === permissionName);
}

/**
 * Check if user has permission for a specific resource and action
 */
export function hasResourcePermission(
  permissions: UserPermission[],
  resource: ResourceType,
  action: ActionType
): boolean {
  return permissions.some(
    perm => perm.resource === resource && perm.action === action
  );
}
